import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { CheckCircle2, CircleSlash } from "lucide-react";
import type { WorkspaceAsset } from "@shared/schema";
import { SectionHeading } from "./item-ui";
import { platformConnected, wordpressConfigured } from "./use-studio";

function ConnectionRow({ name, connected, detail }: { name: string; connected: boolean; detail?: string }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card/60 px-4 py-3" data-testid={`connection-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{name}</p>
        {detail && <p className="break-all text-xs text-muted-foreground">{detail}</p>}
      </div>
      {connected ? (
        <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300">
          <CheckCircle2 size={15} /> Connected
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <CircleSlash size={15} /> Not connected
        </span>
      )}
    </li>
  );
}

/** Read-only: what is connected, the brand basics and uploaded photos. Changing any of it happens in Settings. */
export function BrandTab({ workspaceId, workspace }: { workspaceId: number; workspace: any }) {
  const { data: assets, isLoading: assetsLoading } = useQuery<WorkspaceAsset[]>({
    queryKey: ["/api/workspaces", workspaceId, "assets"],
    enabled: !!workspaceId,
  });
  const { data: brandKit } = useQuery<any>({
    queryKey: ["/api/workspaces", workspaceId, "brand-kit"],
    enabled: !!workspaceId,
  });

  const images = (assets || []).filter((a) => a.type === "image");
  const colors = [brandKit?.primaryColor, brandKit?.secondaryColor, brandKit?.accentColor].filter(
    (c): c is string => typeof c === "string" && /^#[0-9a-f]{3,8}$/i.test(c),
  );

  return (
    <div className="mx-auto max-w-3xl space-y-8" data-testid="brand-tab">
      <section className="space-y-3">
        <SectionHeading title="Where things get published" />
        <ul className="space-y-2">
          <ConnectionRow name="WordPress website" connected={wordpressConfigured(workspace)} detail={workspace?.wordpressUrl || undefined} />
          <ConnectionRow name="LinkedIn" connected={platformConnected(workspace, "linkedin")} />
          <ConnectionRow name="Facebook and Instagram" connected={platformConnected(workspace, "facebook")} />
          <ConnectionRow name="X" connected={platformConnected(workspace, "x")} />
        </ul>
        <p className="text-xs text-muted-foreground">
          Posts for a network that isn't connected can still be approved. You copy the text and post it yourself.{" "}
          <Link href="/settings" className="text-blue-400 underline underline-offset-2" data-testid="link-settings">
            Change connections in Settings
          </Link>
        </p>
      </section>

      <section className="space-y-3" data-testid="brand-basics">
        <SectionHeading title="Brand basics" />
        <div className="space-y-3 rounded-xl border border-border bg-card/60 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {brandKit?.logoUrl ? (
              <img src={brandKit.logoUrl} alt="Logo" className="h-12 max-w-[10rem] rounded bg-white/90 object-contain p-1" />
            ) : (
              <p className="text-sm text-muted-foreground">No logo uploaded.</p>
            )}
            {colors.length > 0 && (
              <div className="flex items-center gap-1.5" aria-label="Brand colours">
                {colors.map((c) => (
                  <span key={c} className="h-6 w-6 rounded-full border border-border" style={{ background: c }} title={c} />
                ))}
              </div>
            )}
          </div>
          {brandKit?.tagline && <p className="text-sm text-foreground/90">{brandKit.tagline}</p>}
          {brandKit?.brandVoice && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground/80">Voice: </span>
              {brandKit.brandVoice}
            </p>
          )}
        </div>
      </section>

      <section className="space-y-3" data-testid="brand-photos">
        <SectionHeading title="Photos and logos" count={images.length} />
        {assetsLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : images.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            No photos or logos uploaded yet.
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {images.slice(0, 24).map((a) => (
              <li key={a.id} className="aspect-square overflow-hidden rounded-lg border border-border bg-muted/30">
                <img src={a.url} alt={a.name} loading="lazy" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">
          To upload or remove photos, use the{" "}
          <a href="/content?legacy=1&tab=library" className="text-blue-400 underline underline-offset-2" data-testid="link-upload-photos">
            advanced view
          </a>
          .
        </p>
      </section>
    </div>
  );
}
